/** As camadas do ecrã: que janela fica por cima de qual.
 *
 *  Antes cada família de janelas tinha a sua conta (os tópicos BCF a
 *  partir de 100, as Propriedades flutuantes a partir de 100 noutra
 *  conta, os painéis flutuantes todos em 50, os destacados em 90) e as
 *  janelas de diálogo (partilhar, recuperar a sessão) ficavam abaixo de
 *  algumas delas. Clicar numa janela não a trazia sempre para a frente,
 *  e um diálogo podia abrir por baixo de uma janela, com o fundo
 *  escurecido a misturar-se com o que estava por trás. Pedido do Rui,
 *  15/10/2026: "algumas janelas às vezes ficam por trás ... e fica
 *  visualização misturada ... meio translúcida".
 *
 *  Agora há uma só ordem por grupo, e bandas fixas por cima:
 *    - `pagina`: as janelas flutuantes da página, de 200 para cima, pela
 *      ordem em que foram tocadas (a última tocada fica à frente);
 *    - `canvas`: os painéis que se arrastam dentro da vista 3D, de 40
 *      para cima (abaixo da revisão de PDF, que cobre a vista);
 *    - menus (900), diálogos (1000) e avisos (1100) ficam sempre por
 *      cima de qualquer janela.
 *
 *  O índice de cada janela é a posição na ordem, por isso não cresce
 *  sem fim por mais cliques que haja. */
import { useEffect, useId, useSyncExternalStore } from 'react'

export const CAMADA = {
  canvas: 40,
  pagina: 200,
  menus: 900,
  dialogos: 1000,
  avisos: 1100,
} as const

export type GrupoDeJanelas = 'pagina' | 'canvas'

const ordens: Record<GrupoDeJanelas, string[]> = { pagina: [], canvas: [] }
const ouvintes = new Set<() => void>()
let versao = 0

function avisar() {
  versao += 1
  for (const f of ouvintes) f()
}

/** Põe a janela à frente das outras do mesmo grupo. */
export function trazerParaAFrente(id: string, grupo: GrupoDeJanelas = 'pagina'): void {
  const o = ordens[grupo]
  if (o[o.length - 1] === id) return
  const i = o.indexOf(id)
  if (i >= 0) o.splice(i, 1)
  o.push(id)
  avisar()
}

/** A janela fechou: sai da ordem. */
export function largarJanela(id: string, grupo: GrupoDeJanelas = 'pagina'): void {
  const o = ordens[grupo]
  const i = o.indexOf(id)
  if (i < 0) return
  o.splice(i, 1)
  avisar()
}

/** O z-index da janela agora. Uma janela que ainda não entrou na ordem
 *  fica na base do grupo. */
export function zDaJanela(id: string, grupo: GrupoDeJanelas = 'pagina'): number {
  const i = ordens[grupo].indexOf(id)
  return CAMADA[grupo] + (i < 0 ? 0 : i + 1)
}

function subscrever(f: () => void) {
  ouvintes.add(f)
  return () => {
    ouvintes.delete(f)
  }
}

/** Para uma janela flutuante: o z-index e a função que a traz para a
 *  frente (para `onPointerDownCapture` na caixa da janela). Entra à
 *  frente quando abre e sai da ordem quando fecha. */
export function useJanelaNaFrente(
  idDado?: string,
  grupo: GrupoDeJanelas = 'pagina',
): { z: number; trazer: () => void } {
  const automatico = useId()
  const id = idDado ?? automatico
  useSyncExternalStore(subscrever, () => versao)
  useEffect(() => {
    trazerParaAFrente(id, grupo)
    return () => largarJanela(id, grupo)
  }, [id, grupo])
  return { z: zDaJanela(id, grupo), trazer: () => trazerParaAFrente(id, grupo) }
}

/** Só para os testes. */
export function limparCamadas(): void {
  ordens.pagina = []
  ordens.canvas = []
  avisar()
}
